import { z } from 'zod';
/**
 * Statuses manager (V38 Settings → Statuses). A per-pool, ordered list of
 * configurable statuses — each with a colour, a behaviour mode, and the activity
 * type it logs as. Seven independent pools (one per nav contact type); the "New"
 * status is seeded system (deletable, name-locked).
 */
/** The seven status pools, with the manager's Contact Type labels. */
export const STATUS_POOLS = [
    { key: 'patient', label: 'Clients' },
    { key: 'prospective_patients', label: 'Prospective Clients' },
    { key: 'inactive_clients', label: 'Inactive Clients' },
    { key: 'referral_source', label: 'Referral Sources' },
    { key: 'prospective_referral_source', label: 'Prospective Referral Sources' },
    { key: 'staff', label: 'Staff' },
    { key: 'prospective_staff', label: 'Prospective Staff' },
];
export const STATUS_POOL_KEYS = [
    'patient',
    'prospective_patients',
    'inactive_clients',
    'referral_source',
    'prospective_referral_source',
    'staff',
    'prospective_staff',
];
export function isStatusPool(k) {
    return STATUS_POOL_KEYS.includes(k);
}
export const STATUS_MODES = ['counter', 'flag'];
/** Activity type a status logs as (the "Log as activity type" pills). */
export const STATUS_NOTE_TYPES = [
    'call',
    'email',
    'meeting',
    'outreach',
    'social',
    'note',
    'other',
];
/** The colour swatch grid in the manager (V38 CHANNEL_COLOR_PALETTE). */
export const STATUS_COLOR_PALETTE = [
    '#1a8a66',
    '#22a87e',
    '#3dbd94',
    '#0e7490',
    '#0a66c2',
    '#2563eb',
    '#7c3aed',
    '#9333ea',
    '#c026d3',
    '#db2777',
    '#e11d48',
    '#c0392b',
    '#dc2626',
    '#ea580c',
    '#d97706',
    '#f59e0b',
    '#ca8a04',
    '#65a30d',
    '#16a34a',
    '#0d9488',
    '#475569',
    '#527060',
    '#334155',
    '#000000',
    '#8aaa9a',
    '#dde8e3',
    '#ffffff',
];
const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Color must be a hex like #1a8a66');
export const contactStatusCreateSchema = z.object({
    label: z.string().min(1).max(80),
    color: hex.default('#1a8a66'),
    mode: z.enum(STATUS_MODES).default('counter'),
    noteType: z.enum(STATUS_NOTE_TYPES).default('note'),
});
/** Patch: system rows accept everything except a label change (server-enforced). */
export const contactStatusUpdateSchema = z
    .object({
    label: z.string().min(1).max(80).optional(),
    color: hex.optional(),
    mode: z.enum(STATUS_MODES).optional(),
    noteType: z.enum(STATUS_NOTE_TYPES).optional(),
    position: z.number().int().min(0).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export const contactStatusReorderSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
});
// ── Contact context menu (V38 reconciliation) ────────────────────────────────
/** Maps a contact's category to its status pool (the mockup's actionType pool). */
export const CATEGORY_TO_POOL = {
    client: 'patient',
    referral_source: 'referral_source',
    staff: 'staff',
};
/**
 * Maps a system contact type (by its name) to its own status pool, so each of the
 * seven types drives its own pipeline. The four prospective/inactive types are the
 * ones a 3-value category can't distinguish. Custom (non-system) types are not
 * listed and fall back to their category pool.
 */
export const TYPE_NAME_TO_POOL = {
    Client: 'patient',
    'Prospective Client': 'prospective_patients',
    'Inactive Client': 'inactive_clients',
    'Referral Source': 'referral_source',
    'Prospective Referral Source': 'prospective_referral_source',
    Staff: 'staff',
    'Prospective Staff': 'prospective_staff',
};
/**
 * Resolve a contact's status pool: prefer its contact type (all seven pipelines),
 * and fall back to its category for legacy contacts with no type or custom types.
 */
export function poolForContact(typeName, category) {
    if (typeName && TYPE_NAME_TO_POOL[typeName])
        return TYPE_NAME_TO_POOL[typeName];
    const cat = category;
    return (cat && CATEGORY_TO_POOL[cat]) || 'patient';
}
/** Set the contact's current pool status (STATUSES ladder click). */
export const contactStatusSetSchema = z.object({
    statusId: z.string().uuid(),
});
/** Fire a saved action from the menu (logs a touch + moves the ladder). */
export const contactFireActionSchema = z.object({
    actionId: z.string().uuid(),
    note: z.string().max(2000).optional(),
});
//# sourceMappingURL=contact-statuses.js.map